import React, { useState, useEffect } from 'react';
import projectService from '../../../services/projectService';
import logger from '../../../utils/logger';
import { Button, Spinner } from '../../ui';

const LeaveNoteCard = ({ projectId, initialNote = '' }) => {
  const [note, setNote] = useState(initialNote);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Update note when initialNote changes
  useEffect(() => {
    setNote(initialNote);
    setHasUnsavedChanges(false);
  }, [initialNote]);
  
  // Track if note has changed from initial value
  useEffect(() => {
    setHasUnsavedChanges(note !== initialNote);
  }, [note, initialNote]);
  
  const handleSaveNote = async () => {
    if (!projectId) {
      logger.error('No project ID provided for note saving');
      return;
    }
    
    setIsSaving(true);
    try {
      await projectService.updateProjectNote(projectId, note);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      logger.log('✅ Note saved successfully');
    } catch (error) {
      logger.error('Error saving note:', error);
      alert('Failed to save note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  const formatTime = (date) => {
    if (!date) return '';
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <div>
      <div className="p-2 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">Leave note</h3>
      </div>
      <div className="p-2">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add your notes here..."
          className="w-full h-24 text-xs border border-border bg-background text-foreground rounded-sm p-2 focus:ring-2 focus:ring-ring focus:border-ring resize-none"
          disabled={isSaving}
        />
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            {isSaving && (
              <div className="flex items-center gap-1 text-xs text-sky-600 dark:text-sky-400">
                <Spinner size="xs" className="text-sky-600 dark:text-sky-400" />
                <span>Saving...</span>
              </div>
            )}
            {!isSaving && lastSaved && (
              <div className="text-xs text-muted-foreground">
                Last saved: {formatTime(lastSaved)}
              </div>
            )}
            {!isSaving && hasUnsavedChanges && (
              <div className="text-xs text-amber-600 dark:text-amber-400">
                Unsaved changes
              </div>
            )}
          </div>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleSaveNote}
            disabled={isSaving || !hasUnsavedChanges}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LeaveNoteCard;

